<template>
  <div>
    <div class="card mb-3" style="width: 100%">
      <div class="card-header bg-light">
        <h3 class="title fw-normal mb-0 p-2">Export or Import Application</h3>
      </div>

      <div class="form-wrapper">
        <el-form ref="form">
          <el-row type="flex" align="middle" justify="start" :gutter="10">
            <!-- Export Button with Label -->
            <div class="action-group">
              <span class="label-text">Export</span>
              <el-button
                class="import-export-btn"
                type="primary"
                size="mini"
                @click="openExportModal"
              >
                <i class="fa fa-cloud-upload"></i>
              </el-button>
            </div>

            <!-- Import Button with Label -->
            <div class="action-group">
              <span class="label-text">Import</span>
              <el-button
                class="import-export-btn"
                type="primary"
                size="mini"
                @click="importDialogVisible = true"
              >
                <i class="fa fa-cloud-download"></i>
              </el-button>
            </div>
          </el-row>
        </el-form>
        <div class="form-note">
          <strong style="color: red">Note:</strong>
          <p>
            You can
            <strong>export the current application</strong>
            as a
            <strong>file</strong>
            by setting an encryption key and entering your current workspace password.
          </p>
          <p>
            To
            <strong>import</strong>
            , upload the file and enter the same encryption key. This will restore the
            <strong>skeleton structure</strong>
            of the workspace, excluding actual data.
          </p>
        </div>
      </div>

      <dialog-component
        :title="'Export Workspace'"
        :visible="exportModalVisible"
        :containerWidth="'400px'"
        @close="exportModalVisible = false"
        @before-close="resetDialog"
      >
        <div>
          <div v-if="exportModalLoading" class="export-loading">
            <el-spinner type="spinner" />
            Exporting workspace, please wait...
          </div>

          <div class="mt-2 mb-2 ml-2 mr-2">
            <label for="workspace-password" class="form-label">Workspace Password :</label>
            <el-input
              v-model="exportPassword"
              type="password"
              placeholder="Enter your Workspace password"
              show-password
            />
          </div>

          <div class="mt-2 mb-2 ml-2 mr-2">
            <label for="encryption-key" class="form-label">Encryption Key :</label>
            <el-input
              v-model="exportKey"
              type="password"
              placeholder="Enter your Key to Encrypt File"
              show-password
            />
            <small class="input-example">Example: mySecretKey123</small>
          </div>
        </div>

        <template #footer>
          <el-button @click="resetDialog" :disabled="exportModalLoading">Cancel</el-button>
          <el-button type="primary" @click="exportWorkspace" :loading="exportModalLoading">
            Export
          </el-button>
        </template>
      </dialog-component>
      <dialog-component
        :title="'Import Workspace'"
        :visible="importDialogVisible"
        :containerWidth="'600px'"
        @close="importDialogVisible = false"
        @before-close="resetDialog"
      >
        <div class="dialog-body-wrapper">
          <div v-if="loading" class="export-loading">
            <div class="loader"></div>
            <p>Importing application...</p>
          </div>

          <div :class="['dialog-content-wrapper', { 'blurred-content': loading }]">
            <div class="mb-3">
              <label class="form-label">Import Option:</label>
              <el-radio-group v-model="importOption">
                <el-radio label="current">Current Workspace/Company</el-radio>
                <el-radio label="new">Create New Workspace/Company</el-radio>
              </el-radio-group>
            </div>

            <div v-if="importOption === 'new'" class="mb-3">
              <label for="workspace-name" class="form-label">New Company Name:</label>
              <el-input v-model="newWorkspaceName" placeholder="Enter new workspace name" />
            </div>
            <div class="form-row">
              <label for="upload-file" class="form-label">Select File :</label>
              <el-upload
                :show-file-list="false"
                :before-upload="handleFileSelect"
                accept=".enc,.json.enc"
                action=""
              >
                <el-button type="primary">Choose File</el-button>
              </el-upload>
            </div>
            <div
              v-if="fileName"
              class="file-preview mt-2"
              style="display: flex; align-items: center"
            >
              <p>
                <span class="bold">File:</span>
                {{ fileName }}
              </p>
              <el-button
                type="danger"
                icon="el-icon-delete"
                size="mini"
                @click="removeFile"
                class="ml-2"
              />
            </div>
            <div class="mt-2">
              <label for="decrypt-key" class="form-label">Decryption Key :</label>
              <el-input
                v-model="exportKey"
                type="password"
                placeholder="Enter your Key to Decrypt File"
                show-password
              />
            </div>
          </div>
        </div>

        <template #footer>
          <el-button @click="importDialogVisible = false" :disabled="loading">Cancel</el-button>
          <el-button type="primary" :disabled="!jsonFile || loading" @click="importJson">
            Import
          </el-button>
        </template>
      </dialog-component>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import axios from '@/config/axios';
  import authHelper from '@/mixins/authHelper';
  import PermissionsHelper from '@/mixins/permissionsHelper';
  import AccessManagement from '@/mixins/AccessManagement';
  import NavigationHelper from '@/mixins/navigationHelper';

  export default {
    name: 'export-or-import-application',
    mixins: [PermissionsHelper, authHelper, AccessManagement, NavigationHelper],
    data() {
      return {
        loading: false,
        exportModalVisible: false,
        exportPassword: '',
        exportKey: '',
        exportModalLoading: false,
        importDialogVisible: false,
        jsonFile: null,
        fileName: '',
        importOption: 'current', // 'current' or 'new'
        newWorkspaceName: '',
      };
    },
    methods: {
      openExportModal() {
        this.exportModalVisible = true;
        this.exportPassword = '';
      },
      async exportWorkspace() {
        this.exportModalLoading = true;
        if (!this.exportPassword) {
          this.$message.error('Please enter your password.');
          this.exportModalLoading = false;
          return;
        }
        try {
          let params = {
            password: this.exportPassword,
            company_id: this.getCompanyDetails._id,
            export_key: this.exportKey,
          };
          let response = await axios.post('/company/export-workspace', params, {
            responseType: 'blob',
          });
          const blob = new Blob([response.data], {
            type: 'application/octet-stream',
          });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `workspace-${this.getCompanyDetails.name}.enc`;
          link.click();
          this.exportModalLoading = false;
          this.exportModalVisible = false;
          this.exportKey = '';
        } catch (err) {
          this.exportModalLoading = false;
          this.exportKey = '';
          let message = err.response?.data?.message || 'Failed to export workspace.';
          this.$message.error(message);
        }
      },
      resetDialog() {
        this.loading = false;
        this.jsonFile = null;
        this.fileName = '';
        this.exportKey = '';
        this.importDialogVisible = false;
        this.exportModalVisible = false;
        this.newWorkspaceName = '';
      },
      handleFileSelect(file) {
        this.jsonFile = file;
        this.fileName = file.name;
        return false; // Prevent auto-upload
      },
      removeFile() {
        this.jsonFile = null;
        this.fileName = '';
      },
      async importJson() {
        if (!this.jsonFile || !this.exportKey) {
          this.$message.error('Please select a file and enter the password.');
          return;
        }
        if (this.importOption === 'new' && !this.newWorkspaceName) {
          this.$message.error('Please enter a new workspace name.');
          return;
        }
        this.loading = true;
        if (this.importOption === 'new' && this.newWorkspaceName) {
          let params = { workspace: this.newWorkspaceName.trim() };
          await this.$store.dispatch('company/fetchCompanyNameStatus', params);
          if (this.getCompanyExistsStatus) {
            this.dialogLoading = false;
            if (!this.getCompanyExistsStatus.success) {
              this.loading = false;
              return this.$notify.error({
                title: 'Error',
                message: this.getCompanyExistsStatus.message,
              });
            }
          }
        }
        try {
          const formData = new FormData();
          formData.append('files', this.jsonFile);
          formData.append('key', this.exportKey);
          formData.append('company_id', this.getCompanyDetails._id || this.getActiveWorkspace._id);
          formData.append('import_option', this.importOption);
          formData.append('new_workspace_name', this.newWorkspaceName);

          const response = await axios.post('/company/import-workspace', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          if (response.data && response.data.success) {
            this.$message.success('Application imported successfully!');
            this.resetDialog();
            if (this.importOption == 'new') {
              this.changeWorkspace(response.data?.data?.companyId);
            } else {
              this.$router.push({ path: '/dashboard' });
              window.location.reload();
            }
          } else {
            this.loading = false;
            this.$message.error(
              `Import failed Due to: ${response.data.message || 'Invalid Details'}`
            );
          }
        } catch (err) {
          const message = err.response?.data?.message || 'Failed to import JSON.';
          this.loading = false;
          this.$message.error(message);
        }
      },
    },
    computed: {
      ...mapGetters('auth', [
        'getAuthenticatedUser',
        'getActiveWorkspace',
        'getUserType',
        'getWorkspaceList',
        'getAuthenticationStatus',
      ]),
      ...mapGetters('company', [
        'getCompanyDetails',
        'getUpdateCompany',
        'getCompanyLogo',
        'getSingleCompanyLogo',
        'getCompanyExistsStatus',
      ]),
    },
  };
</script>

<style scoped lang="scss">
  .card-body {
    padding: 25px;
  }
  .icon-container {
    text-align: center;
  }
  .form-wrapper {
    border: 1px solid #dcdfe6;
    border-radius: 8px;
    padding: 20px;
    background-color: #fff;
    margin-top: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
  .import-export-btn {
    font-family: var(--font-family);
    padding: 6px 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    min-height: 32px;
    margin: 10% 10% 10% 1%;
    padding: 2% 2% 2% 2%;
  }

  .import-export-btn i {
    font-size: 14px;
  }
  .dialog-content-wrapper {
    padding: 16px;
    background-color: #f9f9f9;
    border-radius: 8px;
    transition: filter 0.3s ease;
  }

  .file-preview {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #eef0f3;
    padding: 8px 12px;
    border-radius: 6px;
  }

  .action-group {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-right: 20px;
  }

  .label-text {
    font-size: 14px;
    font-weight: 500;
  }
  .form-note {
    margin-top: 20px;
    padding: 16px;
    background-color: #fdf6ec;
    border-left: 5px solid #e6a23c;
    border-radius: 4px;
    font-size: 14px;
    color: #8a6d3b;
    line-height: 1.6;
  }
  .form-row {
    display: flex;
    align-items: center;
    margin-top: 12px;
    gap: 12px;
  }
  .form-label {
    display: block;
    margin-bottom: 4px;
    font-weight: 500;
  }

  .input-example {
    color: #888;
    font-size: 12px;
    margin-top: 4px;
    display: block;
  }

  .export-loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    color: #fff;
    background: rgba(0, 0, 0, 0.7);
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }
  .loader {
    width: 50px;
    aspect-ratio: 1;
    border-radius: 50%;
    border: 8px solid lightblue;
    border-right-color: orange;
    animation: l2 1s infinite linear;
  }

  @keyframes l2 {
    to {
      transform: rotate(1turn);
    }
  }

  .blurred-content {
    pointer-events: none;
    opacity: 0.5;
  }
</style>
